import styled from '@emotion/styled'
import { onFormatDate } from 'components'
import {
    Status1Icon,
    Status2Icon,
    Status3Icon,
} from 'components/CustomIcons/StatusIcons'
import {
    ColumnChangeWrapper,
    EntityChangeList,
    EntityChangeListProps,
} from 'components/EntityChangeList'
import {
    ColumnRenderer,
    renderDate,
    renderEnum,
    renderMandatory,
    renderPersona,
} from 'components/EntityList'
import { Box } from 'grid'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { TFunction } from 'i18next'
import {
    BookingStatus,
    FlightType,
    Participant,
    Proving,
    ProvingChange,
    ProvingChangeOperation,
    ProvingType,
    TrackHotel,
} from 'model'
import {
    ConstrainMode,
    Icon,
    mergeStyles,
    PersonaSize,
    SelectionMode,
    Stack,
    TooltipHost,
} from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import { createFieldColumn, getParticipantTypeDisplayName } from 'views/helpers'
import { useDoubleBookingsParticipantsListQuery } from 'views/ProvingDetailsView/hooks'

const renderFerry: (t: TFunction) => ColumnRenderer =
  (t: TFunction) => (item: Participant, i, props) => {
    if (item.ferry && item.ferry.outward && item.ferry.return) {
      const content = `${t('field.ferryConnection.outward.label')}: ${
        item.ferry.outward.title
      }\n${t('field.ferryConnection.return.label')}: ${item.ferry.return.title}`
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
    } else if (item.ferry && item.ferry.outward) {
      const content = `${t('field.ferryConnection.outward.label')}: ${
        item.ferry.outward.title
      }`
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
    } else if (item.ferry && item.ferry.return) {
      const content = `${t('field.ferryConnection.return.label')}: ${
        item.ferry.return.title
      }`
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
    } else {
      return ''
    }
  }

const StyledBox = styled(Box)`
  padding-top: 3px;
`

function TrackingHotelWrapper({ item, trackHotel }: any) {
  const [change, setChange] = React.useState<ProvingChange | null>(null)

  React.useEffect(() => {
    const foundChange = item._changes?.changes?.find(
      (c: any) =>
        c.change.path.includes(trackHotel?._id!) ||
        c.change.key === trackHotel?._id!
    )

    setChange(foundChange)
  }, [item, trackHotel])

  const oldTrackHotel = change
    ? change.change.type === ProvingChangeOperation.Add
      ? change.change.value
      : {
          ...trackHotel,
          [change.change.key]:
            change.change.type === ProvingChangeOperation.Remove
              ? undefined
              : change.change.oldValue,
        }
    : trackHotel

  return change ? (
    <ColumnChangeWrapper
      original={<TrackingHotel trackHotel={oldTrackHotel} />}
      change={change}
    >
      <TrackingHotel trackHotel={trackHotel} />
    </ColumnChangeWrapper>
  ) : (
    <TrackingHotel trackHotel={trackHotel} />
  )
}

function TrackingHotel({ trackHotel }: { trackHotel: TrackHotel }) {
  if (!trackHotel) {
    // tslint:disable-next-line:no-console
    console.warn('trackHotel is undefined - this should not be')
    return <></>
  }

  return (
    <Box style={{ whiteSpace: 'pre-line' }}>
      {trackHotel.bookingStatus === BookingStatus.Open ? (
        <Status1Icon />
      ) : trackHotel.bookingStatus === BookingStatus.WorkInProgress ? (
        <Status2Icon />
      ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
        <Status3Icon />
      ) : (
        <></>
      )}{' '}
      {trackHotel.hotel
        ? `${trackHotel.hotel?.title} ${onFormatDate(
            new Date(trackHotel?.dateFrom)
          )} - ${onFormatDate(new Date(trackHotel?.dateTo))}`
        : typeof trackHotel === 'string'
        ? trackHotel
        : ''}
    </Box>
  )
}

export type ParticipantListProps = EntityChangeListProps<
  Partial<Participant> & { _id: string }
> & {
  proving?: Proving
  provingGround: any
  params?: any
  participants?: any
}

export const ParticipantList: React.FunctionComponent<ParticipantListProps> = ({
  items,
  provingGround,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [proving] = useCurrentProving()
  const { data, loading } = useDoubleBookingsParticipantsListQuery()
  const fieldColumn = React.useMemo(() => createFieldColumn(t), [t])
  const result =
    !loading && data && data.cachedProvings ? data.cachedProvings : []
  const checkForDoublebooking = React.useCallback(
    (ppl: Participant) => {
      const doublebookings = [] as any[]
      const people = ppl || []
      result.forEach((prov) =>
        (prov.participants || []).forEach((participant: any) => {
          if (
            participant &&
            people &&
            people.person &&
            (participant.person ? participant.person._id : '') ===
              people.person._id
          ) {
            if (
              participant.dateFrom <= people.dateTo &&
              participant.dateTo >= people.dateFrom
            ) {
              doublebookings.push({
                person: people,
              })
            }
          }
        })
      )
      let count = 0
      doublebookings.forEach((test) => {
        if (test.person && test.person.person && people.person) {
          if (test.person.person._id === people.person._id) {
            count++
            return count
          } else {
            return count
          }
        } else {
          return count
        }
      })
      return count
    },
    [result]
  )
  const iconClassRed = mergeStyles({
    color: theme.palette.red,
  })

  const createTrackHotelColumns = (direction: 'outward' | 'return') => {
    return Array.from({ length: 10 }, (_, j) =>
      fieldColumn(`trackHotels.${direction}[${j}]`, {
        useFullFieldname: true,
        minWidth: 155,
        ungroupable: true,
        onRender: (item: Participant, i, col) => {
          const trackHotel = item.trackHotels?.[direction]?.[j]
          if (trackHotel?.hotel) {
            return (
              <TrackingHotelWrapper item={item} trackHotel={trackHotel}>
                {direction === 'return' && (
                  <Box style={{ whiteSpace: 'pre-line' }}>
                    {trackHotel.bookingStatus === BookingStatus.Open ? (
                      <Status1Icon />
                    ) : trackHotel.bookingStatus ===
                      BookingStatus.WorkInProgress ? (
                      <Status2Icon />
                    ) : trackHotel.bookingStatus === BookingStatus.Finished ? (
                      <Status3Icon />
                    ) : (
                      <></>
                    )}{' '}
                    {trackHotel.hotel.title}{' '}
                    {onFormatDate(new Date(trackHotel.dateFrom))} -{' '}
                    {onFormatDate(new Date(trackHotel.dateTo))}
                  </Box>
                )}
              </TrackingHotelWrapper>
            )
          }
          if (trackHotel?._id) {
            return <TrackingHotelWrapper item={item} trackHotel={trackHotel} />
          }
          return <></>
        },
      })
    )
  }
  const trackHotelOutwardColumns = createTrackHotelColumns('outward')
  const trackHotelReturnColumns = createTrackHotelColumns('return')

  function pushColumnsIfConditionMet(
    trackHotelColumns: any[],
    direction: string,
    cols: any[]
  ) {
    trackHotelColumns.forEach((column, i) => {
      const hasRelevantHotel = proving.participants?.some((participant) => {
        const hasHotel = participant.trackHotels?.[direction]?.[i]
        const hasChanges = participant._changes?.changes?.some(
          (change) =>
            change.change.relativePath?.includes(direction) &&
            change.change.value?.index - 1 === i
        )
        return hasHotel || hasChanges
      })

      if (hasRelevantHotel) cols.push(column)
    })
  }

  const columns = React.useMemo(() => {
    const cols = [
      fieldColumn('person', {
        onRender: (item: Participant, i, col) => {
          if (
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === true &&
              item.unknownPerson.personType === 'user') ||
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === true &&
              item.unknownPerson.personType === 'external')
          ) {
            return t('model.proving.participant.participantUnknown')
          } else if (
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === false &&
              item.unknownPerson.personType === 'user') ||
            (!item.person &&
              item.unknownPerson &&
              item.unknownPerson.isUnknown === false &&
              item.unknownPerson.personType === 'external')
          ) {
            return `${item.unknownPerson.surName}, ${item.unknownPerson.firstName}`
          } else if (
            item.unknownPerson &&
            item.unknownPerson.isUnknown &&
            item.unknownPerson.personType === 'worker'
          ) {
            return item.percentage + '%, ' + item.unknownPerson.department
          } else if (
            item.unknownPerson &&
            item.unknownPerson.isUnknown &&
            item.unknownPerson.personType === 'supplier'
          ) {
            return item.unknownPerson.supplier
              ? `${item.unknownPerson.surName}, ${item.unknownPerson.firstName} - ${item.unknownPerson.supplier}`
              : '[Teilnehmer noch unbekannt]'
          } else if (
            item.unknownPerson &&
            !item.unknownPerson.isUnknown &&
            item.unknownPerson.personType === 'worker'
          ) {
            return (
              <Stack horizontal={true}>
                {checkForDoublebooking(item) > 1 && (
                  <TooltipHost content={'Doppelbuchung'}>
                    <Stack.Item>
                      <StyledBox>
                        <Icon iconName="Important" className={iconClassRed} />
                      </StyledBox>
                    </Stack.Item>
                  </TooltipHost>
                )}
                <Stack.Item>
                  {renderPersona({
                    size: PersonaSize.size24,
                  })(item, i, col)}
                </Stack.Item>
                <Stack.Item>
                  <StyledBox>{item.percentage}% </StyledBox>
                </Stack.Item>
              </Stack>
            )
          } else if (item.headOfProving && item.headOfProving.isHeadOfProving) {
            return (
              <Stack horizontal={true}>
                {checkForDoublebooking(item) > 1 && (
                  <TooltipHost content={'Doppelbuchung'}>
                    <Stack.Item>
                      <StyledBox>
                        <Icon iconName="Important" className={iconClassRed} />
                      </StyledBox>
                    </Stack.Item>
                  </TooltipHost>
                )}
                <Stack.Item>
                  {renderPersona({
                    size: PersonaSize.size24,
                  })(item, i, col)}
                </Stack.Item>
                <TooltipHost
                  content={
                    t('model.proving.participant.headOfProving') +
                    `: ${onFormatDate(
                      new Date(item.headOfProving.dateFrom)
                    )} - ${onFormatDate(new Date(item.headOfProving.dateTo))}`
                  }
                >
                  <Stack.Item>
                    <StyledBox>
                      <Icon iconName="PartyLeader" />
                    </StyledBox>
                  </Stack.Item>
                </TooltipHost>
              </Stack>
            )
          } else {
            return (
              <Stack horizontal={true}>
                {checkForDoublebooking(item) > 1 && (
                  <TooltipHost content={'Doppelbuchung'}>
                    <Stack.Item>
                      <StyledBox>
                        <Icon iconName="Important" className={iconClassRed} />
                      </StyledBox>
                    </Stack.Item>
                  </TooltipHost>
                )}
                <Stack.Item>
                  {renderPersona({
                    size: PersonaSize.size24,
                  })(item, i, col)}
                </Stack.Item>
              </Stack>
            )
          }
        },
        minWidth: 200,
        ungroupable: true,
      }),
      fieldColumn('person.type', {
        minWidth: 120,
        isGrouped: true,
        getGroupValue: (item: Participant) =>
          getParticipantTypeDisplayName(item, t),
        onRender: (item: Participant, i, col) =>
          getParticipantTypeDisplayName(item, t),
        useFullFieldname: true,
      }),
      fieldColumn('dateFrom', { onRender: renderDate, ungroupable: true }),
      fieldColumn('dateTo', { onRender: renderDate, ungroupable: true }),
      fieldColumn('orderId', {
        onRender: (item: Participant, i, col) => {
          if (
            // (!item.orderId &&
            //   item.flight &&
            //   item.flight.flightType !== FlightType.NoFlight) ||
            // (!item.orderId &&
            //   item.rentalCar &&
            //   item.rentalCar.carType !== CarType.None) ||
            // (!item.orderId &&
            //   item.person &&
            //   item.person.type === PersonType.Worker) ||
            // (!item.orderId &&
            //   item.unknownPerson &&
            //   item.unknownPerson.personType === PersonType.Worker) ||
            // (!item.orderId &&
            //   ((item.accomodation &&
            //     item.accomodation.accomodationType ===
            //       AccomodationType.AccomodationWithPreference) ||
            //     (item.accomodation &&
            //       item.accomodation.accomodationType ===
            //         AccomodationType.NoPreference)))
            !item.orderId
          ) {
            return renderMandatory(item, i, col)
          } else {
            return item.orderId
          }
        },
      }),
      fieldColumn('accomodation.accomodation', {
        onRender: (item: Participant, i, col) => {
          if (item.accomodation && item.accomodation.bookingStatus) {
            switch (item.accomodation.bookingStatus) {
              case 'open':
                return (
                  <Box>
                    <Status1Icon />{' '}
                    {item &&
                    item.accomodation &&
                    item.accomodation.accomodation &&
                    item.accomodation.accomodation.title
                      ? item.accomodation.accomodation.title
                      : renderEnum(t, 'accomodationType')(item, i, col)}
                    <div>
                      {item &&
                      item.accomodation &&
                      item.accomodation.dateFrom &&
                      item.accomodation.dateFrom !== item.dateFrom
                        ? `Check-In: ${onFormatDate(
                            new Date(item.accomodation.dateFrom)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item &&
                      item.accomodation &&
                      item.accomodation.dateTo &&
                      item.accomodation.dateTo !== item.dateTo
                        ? `Check-Out: ${onFormatDate(
                            new Date(item.accomodation.dateTo)
                          )}`
                        : ''}
                    </div>
                  </Box>
                )
              case 'workInProgress':
                return (
                  <Box>
                    <Status2Icon />{' '}
                    {item &&
                    item.accomodation &&
                    item.accomodation.accomodation &&
                    item.accomodation.accomodation.title
                      ? item.accomodation.accomodation.title
                      : renderEnum(t, 'accomodationType')(item, i, col)}
                    <div>
                      {item &&
                      item.accomodation &&
                      item.accomodation.dateFrom &&
                      item.accomodation.dateFrom !== item.dateFrom
                        ? `Check-In: ${onFormatDate(
                            new Date(item.accomodation.dateFrom)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item &&
                      item.accomodation &&
                      item.accomodation.dateTo &&
                      item.accomodation.dateTo !== item.dateTo
                        ? `Check-Out: ${onFormatDate(
                            new Date(item.accomodation.dateTo)
                          )}`
                        : ''}
                    </div>
                  </Box>
                )
              case 'finished':
                return (
                  <Box>
                    <Status3Icon />{' '}
                    {item &&
                    item.accomodation &&
                    item.accomodation.accomodation &&
                    item.accomodation.accomodation.title
                      ? item.accomodation.accomodation.title
                      : renderEnum(t, 'accomodationType')(item, i, col)}
                    <div>
                      {item &&
                      item.accomodation &&
                      item.accomodation.dateFrom &&
                      item.accomodation.dateFrom !== item.dateFrom
                        ? `Check-In: ${onFormatDate(
                            new Date(item.accomodation.dateFrom)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item &&
                      item.accomodation &&
                      item.accomodation.dateTo &&
                      item.accomodation.dateTo !== item.dateTo
                        ? `Check-Out: ${onFormatDate(
                            new Date(item.accomodation.dateTo)
                          )}`
                        : ''}
                    </div>
                  </Box>
                )
              default:
                return item &&
                  item.accomodation &&
                  item.accomodation.accomodation &&
                  item.accomodation.accomodation.title
                  ? item.accomodation.accomodation.title
                  : renderEnum(t, 'accomodationType')(item, i, col)
            }
          }
        },
        getGroupValue: (item: Participant) =>
          t(
            `enum.accomodationType.${
              item.accomodation && item.accomodation.accomodationType
            }`
          ),
        ungroupable: true,
        minWidth: 150,
      }),
      fieldColumn('rentalCar.carType', {
        onRender: (item: Participant, i, col) => {
          switch (item.rentalCar && item.rentalCar.bookingStatus) {
            case 'open':
              return (
                <Box>
                  <Status1Icon /> {renderEnum(t, 'rentalCar')(item, i, col)}
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateFrom &&
                    item.rentalCar.dateFrom !== item.dateFrom
                      ? `Abholung: ${onFormatDate(
                          new Date(item.rentalCar.dateFrom)
                        )}`
                      : ''}
                  </div>
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateTo &&
                    item.rentalCar.dateTo !== item.dateTo
                      ? `Rückgabe: ${onFormatDate(
                          new Date(item.rentalCar.dateTo)
                        )}`
                      : ''}
                  </div>
                </Box>
              )
            case 'workInProgress':
              return (
                <Box>
                  <Status2Icon /> {renderEnum(t, 'rentalCar')(item, i, col)}
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateFrom &&
                    item.rentalCar.dateFrom !== item.dateFrom
                      ? `Abholung: ${onFormatDate(
                          new Date(item.rentalCar.dateFrom)
                        )}`
                      : ''}
                  </div>
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateTo &&
                    item.rentalCar.dateTo !== item.dateTo
                      ? `Rückgabe: ${onFormatDate(
                          new Date(item.rentalCar.dateTo)
                        )}`
                      : ''}
                  </div>
                </Box>
              )
            case 'finished':
              return (
                <Box>
                  <Status3Icon /> {renderEnum(t, 'rentalCar')(item, i, col)}
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateFrom &&
                    item.rentalCar.dateFrom !== item.dateFrom
                      ? `Abholung: ${onFormatDate(
                          new Date(item.rentalCar.dateFrom)
                        )}`
                      : ''}
                  </div>
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateTo &&
                    item.rentalCar.dateTo !== item.dateTo
                      ? `Rückgabe: ${onFormatDate(
                          new Date(item.rentalCar.dateTo)
                        )}`
                      : ''}
                  </div>
                </Box>
              )
            case 'fleetVehicle':
              return (
                <Box>
                  <Status3Icon /> Flottenfahrzeug
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateFrom &&
                    item.rentalCar.dateFrom !== item.dateFrom
                      ? `Abholung: ${onFormatDate(
                          new Date(item.rentalCar.dateFrom)
                        )}`
                      : ''}
                  </div>
                  <div>
                    {item &&
                    item.rentalCar &&
                    item.rentalCar.dateTo &&
                    item.rentalCar.dateTo !== item.dateTo
                      ? `Rückgabe: ${onFormatDate(
                          new Date(item.rentalCar.dateTo)
                        )}`
                      : ''}
                  </div>
                </Box>
              )
            default:
              return renderEnum(t, 'rentalCar')(item, i, col)
          }
        },
        getGroupValue: (item: Participant) =>
          t(`enum.rentalCar.carType.${item.rentalCar!.carType}`),
        ungroupable: true,
      }),
    ]

    return cols
  }, [
    checkForDoublebooking,
    fieldColumn,
    iconClassRed,
    t,
    proving.participants,
  ])

  const flightColumn = fieldColumn('flight.flightType', {
    onRender: (item: Participant, i, col) => {
      switch (item && item.flight && item.flight.bookingStatus) {
        case 'open':
          return (
            <Box>
              <Status1Icon /> {renderEnum(t, 'flight.flightType')(item, i, col)}{' '}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.NoFlight && <div />}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.OutboundFlight && (
                  <div>
                    {item.flight &&
                    item.flight.outboundData &&
                    item.flight.outboundData.departure &&
                    item.flight.outboundData.departure !== item.dateFrom
                      ? `Hinflug: ${onFormatDate(
                          new Date(item.flight.outboundData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.ReturnFlight && (
                  <div>
                    {item.flight &&
                    item.flight.returnData &&
                    item.flight.returnData.departure &&
                    item.flight.returnData.departure !== item.dateTo
                      ? `Rückflug: ${onFormatDate(
                          new Date(item.flight.returnData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.Roundtrip && (
                  <>
                    <div>
                      {item.flight &&
                      item.flight.outboundData &&
                      item.flight.outboundData.departure &&
                      item.flight.outboundData.departure !== item.dateFrom
                        ? `Hinflug: ${onFormatDate(
                            new Date(item.flight.outboundData.departure)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item.flight &&
                      item.flight.returnData &&
                      item.flight.returnData.departure &&
                      item.flight.returnData.departure !== item.dateTo
                        ? `Rückflug: ${onFormatDate(
                            new Date(item.flight.returnData.departure)
                          )}`
                        : ''}
                    </div>
                  </>
                )}
            </Box>
          )
        case 'workInProgress':
          return (
            <Box>
              <Status2Icon /> {renderEnum(t, 'flight.flightType')(item, i, col)}{' '}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.NoFlight && <div />}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.OutboundFlight && (
                  <div>
                    {item.flight &&
                    item.flight.outboundData &&
                    item.flight.outboundData.departure &&
                    item.flight.outboundData.departure !== item.dateFrom
                      ? `Hinflug: ${onFormatDate(
                          new Date(item.flight.outboundData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.ReturnFlight && (
                  <div>
                    {item.flight &&
                    item.flight.returnData &&
                    item.flight.returnData.departure &&
                    item.flight.returnData.departure !== item.dateTo
                      ? `Rückflug: ${onFormatDate(
                          new Date(item.flight.returnData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.Roundtrip && (
                  <>
                    <div>
                      {item.flight &&
                      item.flight.outboundData &&
                      item.flight.outboundData.departure &&
                      item.flight.outboundData.departure !== item.dateFrom
                        ? `Hinflug: ${onFormatDate(
                            new Date(item.flight.outboundData.departure)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item.flight &&
                      item.flight.returnData &&
                      item.flight.returnData.departure &&
                      item.flight.returnData.departure !== item.dateTo
                        ? `Rückflug: ${onFormatDate(
                            new Date(item.flight.returnData.departure)
                          )}`
                        : ''}
                    </div>
                  </>
                )}
            </Box>
          )
        case 'finished':
          return (
            <Box>
              <Status3Icon /> {renderEnum(t, 'flight.flightType')(item, i, col)}{' '}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.NoFlight && <div />}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.OutboundFlight && (
                  <div>
                    {item.flight &&
                    item.flight.outboundData &&
                    item.flight.outboundData.departure &&
                    item.flight.outboundData.departure !== item.dateFrom
                      ? `Hinflug: ${onFormatDate(
                          new Date(item.flight.outboundData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.ReturnFlight && (
                  <div>
                    {item.flight &&
                    item.flight.returnData &&
                    item.flight.returnData.departure &&
                    item.flight.returnData.departure !== item.dateTo
                      ? `Rückflug: ${onFormatDate(
                          new Date(item.flight.returnData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.Roundtrip && (
                  <>
                    <div>
                      {item.flight &&
                      item.flight.outboundData &&
                      item.flight.outboundData.departure &&
                      item.flight.outboundData.departure !== item.dateFrom
                        ? `Hinflug: ${onFormatDate(
                            new Date(item.flight.outboundData.departure)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item.flight &&
                      item.flight.returnData &&
                      item.flight.returnData.departure &&
                      item.flight.returnData.departure !== item.dateTo
                        ? `Rückflug: ${onFormatDate(
                            new Date(item.flight.returnData.departure)
                          )}`
                        : ''}
                    </div>
                  </>
                )}
            </Box>
          )
        case 'notRequired':
          return (
            <Box>
              {renderEnum(t, 'flight.flightType')(item, i, col)}{' '}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.NoFlight && <div />}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.OutboundFlight && (
                  <div>
                    {item.flight &&
                    item.flight.outboundData &&
                    item.flight.outboundData.departure &&
                    item.flight.outboundData.departure !== item.dateFrom
                      ? `Hinflug: ${onFormatDate(
                          new Date(item.flight.outboundData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.ReturnFlight && (
                  <div>
                    {item.flight &&
                    item.flight.returnData &&
                    item.flight.returnData.departure &&
                    item.flight.returnData.departure !== item.dateTo
                      ? `Rückflug: ${onFormatDate(
                          new Date(item.flight.returnData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.Roundtrip && (
                  <>
                    <div>
                      {item.flight &&
                      item.flight.outboundData &&
                      item.flight.outboundData.departure &&
                      item.flight.outboundData.departure !== item.dateFrom
                        ? `Hinflug: ${onFormatDate(
                            new Date(item.flight.outboundData.departure)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item.flight &&
                      item.flight.returnData &&
                      item.flight.returnData.departure &&
                      item.flight.returnData.departure !== item.dateTo
                        ? `Rückflug: ${onFormatDate(
                            new Date(item.flight.returnData.departure)
                          )}`
                        : ''}
                    </div>
                  </>
                )}
            </Box>
          )
        default:
          return (
            <Box>
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.NoFlight && <div />}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.OutboundFlight && (
                  <div>
                    {item.flight &&
                    item.flight.outboundData &&
                    item.flight.outboundData.departure &&
                    item.flight.outboundData.departure !== item.dateFrom
                      ? `Hinflug: ${onFormatDate(
                          new Date(item.flight.outboundData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.ReturnFlight && (
                  <div>
                    {item.flight &&
                    item.flight.returnData &&
                    item.flight.returnData.departure &&
                    item.flight.returnData.departure !== item.dateTo
                      ? `Rückflug: ${onFormatDate(
                          new Date(item.flight.returnData.departure)
                        )}`
                      : ''}
                  </div>
                )}
              {item.flight &&
                item.flight.flightType &&
                item.flight.flightType === FlightType.Roundtrip && (
                  <>
                    <div>
                      {item.flight &&
                      item.flight.outboundData &&
                      item.flight.outboundData.departure &&
                      item.flight.outboundData.departure !== item.dateFrom
                        ? `Hinflug: ${onFormatDate(
                            new Date(item.flight.outboundData.departure)
                          )}`
                        : ''}
                    </div>
                    <div>
                      {item.flight &&
                      item.flight.returnData &&
                      item.flight.returnData.departure &&
                      item.flight.returnData.departure !== item.dateTo
                        ? `Rückflug: ${onFormatDate(
                            new Date(item.flight.returnData.departure)
                          )}`
                        : ''}
                    </div>
                  </>
                )}
            </Box>
          )
      }
    },
    getGroupValue: (item: Participant) =>
      t(
        `enum.flight.flightType.${
          item && item.flight && item.flight.flightType
        }`
      ),
    ungroupable: true,
  })
  if (!provingGround.hideFlight) {
    columns.push(flightColumn)
  }

  if (!provingGround.hideHotels) {
    pushColumnsIfConditionMet(trackHotelOutwardColumns, 'outward', columns)
    pushColumnsIfConditionMet(trackHotelReturnColumns, 'return', columns)
  }

  const ferryColumn = fieldColumn('ferry', {
    onRender: (item: Participant, i, col) => {
      if (!(item && item.ferry && item.ferry.bookingStatus)) {
        return ''
      }
      switch (item.ferry.bookingStatus) {
        case 'open':
          return (
            <Box>
              <Status1Icon /> {renderFerry(t)(item, i, col)}
            </Box>
          )
        case 'workInProgress':
          return (
            <Box>
              <Status2Icon /> {renderFerry(t)(item, i, col)}
            </Box>
          )
        case 'finished':
          return (
            <Box>
              <Status3Icon /> {renderFerry(t)(item, i, col)}
            </Box>
          )
        default:
          return renderFerry(t)(item, i, col)
      }
    },
    minWidth: 175,
    ungroupable: true,
  })
  if (!provingGround.hideFerryConnection) {
    columns.push(ferryColumn)
  }

  const nordschleifeColumn = fieldColumn('nordschleife', {
    onRender: (item: Participant, i, col) => {
      switch (item && item.nordschleifeBookingStatus) {
        case 'open':
          return (
            <Box>
              <Status1Icon /> {item.nordschleife ? 'Nordschleife' : ''}
            </Box>
          )
        case 'workInProgress':
          return (
            <Box>
              <Status2Icon /> {item.nordschleife ? 'Nordschleife' : ''}
            </Box>
          )
        case 'finished':
          return (
            <Box>
              <Status3Icon /> {item.nordschleife ? 'Nordschleife' : ''}
            </Box>
          )
        default:
          return item.nordschleife ? 'Nordschleife' : ''
      }
    },
    ungroupable: true,
  })
  if (!provingGround.hideNordschleife) {
    columns.push(nordschleifeColumn)
  }

  return (
    <EntityChangeList
      selectionMode={SelectionMode.multiple}
      stickyHeader={true}
      items={items}
      constrainMode={ConstrainMode.horizontalConstrained}
      columns={columns}
      parentGroup={
        (proving.type && proving.type === ProvingType.Block) ||
        proving.enableGroups
          ? 'group'
          : undefined
      }
      onShouldVirtualize={() => false}
      noParentGroupText={t('noGroup')}
      alternate={true}
      {...props}
    />
  )
}
